import React, { useState, useEffect, useCallback } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Loader2, User, FileCheck } from 'lucide-react';
import { zohoAPI } from '@/lib/api';
import { toast } from 'sonner';

function formatDate(d) {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch (e) {
    return d;
  }
}

export default function ZohoIncrementsTable() {
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var dataState = useState([]);
  var data = dataState[0];
  var setData = dataState[1];

  var selectedState = useState(null);
  var selected = selectedState[0];
  var setSelected = selectedState[1];

  var statusFilterState = useState('all');
  var statusFilter = statusFilterState[0];
  var setStatusFilter = statusFilterState[1];

  var letterFilterState = useState('all');
  var letterFilter = letterFilterState[0];
  var setLetterFilter = letterFilterState[1];

  var fetchData = useCallback(async function() {
    setLoading(true);
    try {
      var params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (letterFilter !== 'all') params.letter_signed = letterFilter;
      var res = await zohoAPI.getAcceptedSubmissions(params);
      setData(res.data.submissions || []);
    } catch (err) {
      console.error('Failed to load submissions:', err);
      toast.error('Failed to load increment data');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, letterFilter]);

  useEffect(function() {
    fetchData();
  }, [fetchData]);

  async function handleToggleLetter(subId) {
    try {
      await zohoAPI.toggleLetterSigned(subId);
      toast.success('Letter signed status updated');
      fetchData();
    } catch (err) {
      toast.error('Failed to update');
    }
  }

  var acceptedCount = data.filter(function(d) { return d.approval_status === 'accepted'; }).length;
  var rejectedCount = data.filter(function(d) { return d.approval_status === 'rejected'; }).length;

  return (
    <div data-testid="zoho-increments-table">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold">Increments from Zoho Forms</h3>
          <p className="text-sm text-muted-foreground">{data.length} submission{data.length !== 1 ? 's' : ''}</p>
        </div>
        <div className="flex items-center gap-2">
          {acceptedCount > 0 && (
            <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700 border-emerald-200">{acceptedCount} Accepted</Badge>
          )}
          {rejectedCount > 0 && (
            <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">{rejectedCount} Rejected</Badge>
          )}
        </div>
      </div>

      <IncrFilters
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
        letterFilter={letterFilter}
        onLetterChange={setLetterFilter}
      />

      {loading ? (
        <div className="flex items-center justify-center py-16" data-testid="increments-loading">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : data.length === 0 ? (
        <div className="text-center py-16 text-muted-foreground" data-testid="increments-empty">
          <p className="text-sm">No submissions found matching the filters.</p>
          <p className="text-xs mt-1">Accept or reject submissions from the Zoho Submissions Dashboard.</p>
        </div>
      ) : (
        <IncrTable rows={data} onView={setSelected} onToggleLetter={handleToggleLetter} />
      )}

      {selected && (
        <IncrDetailSheet sub={selected} open={true} onClose={function() { setSelected(null); }} />
      )}
    </div>
  );
}

function IncrFilters({ statusFilter, onStatusChange, letterFilter, onLetterChange }) {
  return (
    <div className="flex items-center gap-3 mb-4" data-testid="incr-filters">
      <Select value={statusFilter} onValueChange={onStatusChange}>
        <SelectTrigger className="w-[160px] h-9 text-sm" data-testid="incr-status-filter">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Status</SelectItem>
          <SelectItem value="accepted">Approved</SelectItem>
          <SelectItem value="rejected">Rejected</SelectItem>
        </SelectContent>
      </Select>

      <Select value={letterFilter} onValueChange={onLetterChange}>
        <SelectTrigger className="w-[200px] h-9 text-sm" data-testid="incr-letter-filter">
          <SelectValue placeholder="Increment Letter Signed" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Letter Status</SelectItem>
          <SelectItem value="yes">Letter Signed</SelectItem>
          <SelectItem value="no">Letter Not Signed</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}

function IncrTable({ rows, onView, onToggleLetter }) {
  return (
    <div className="border border-slate-200 rounded-xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 border-b border-slate-200">
              <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Submission Date</th>
              <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Emp Name</th>
              <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Company</th>
              <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Increment</th>
              <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Effective Date</th>
              <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Status</th>
              <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Letter Signed</th>
              <th className="text-left px-4 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">View</th>
            </tr>
          </thead>
          <IncrTableBody rows={rows} onView={onView} onToggleLetter={onToggleLetter} />
        </table>
      </div>
    </div>
  );
}

function IncrTableBody({ rows, onView, onToggleLetter }) {
  return (
    <tbody>
      {rows.map(function(row) {
        var m = row.mapped || {};
        var isAccepted = row.approval_status === 'accepted';
        var letterSigned = row.letter_signed || false;
        return (
          <tr key={row.id} className="border-b border-slate-100 hover:bg-slate-50/60 transition-colors duration-100" data-testid={'incr-row-' + row.id}>
            <td className="px-4 py-3 text-muted-foreground">{formatDate(m.submission_date)}</td>
            <td className="px-4 py-3 font-medium">{m.emp_name || '-'}</td>
            <td className="px-4 py-3">{m.company || '-'}</td>
            <td className="px-4 py-3 font-medium text-emerald-700">{m.increment || '-'}</td>
            <td className="px-4 py-3">{m.effective_date || '-'}</td>
            <td className="px-4 py-3">
              <Badge variant="outline" className={'text-xs border ' + (isAccepted ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200')}>
                {isAccepted ? 'Approved' : 'Rejected'}
              </Badge>
            </td>
            <td className="px-4 py-3">
              <div className="flex items-center gap-2" onClick={function(e) { e.stopPropagation(); }}>
                <Switch
                  checked={letterSigned}
                  onCheckedChange={function() { onToggleLetter(row.id); }}
                  className="scale-75"
                  data-testid={'letter-toggle-' + row.id}
                />
                <span className={'text-xs font-medium ' + (letterSigned ? 'text-emerald-700' : 'text-muted-foreground')}>
                  {letterSigned ? 'Yes' : 'No'}
                </span>
              </div>
            </td>
            <td className="px-4 py-3">
              <button className="text-xs text-primary font-medium hover:underline" onClick={function() { onView(row); }} data-testid={'incr-view-' + row.id}>
                View Details
              </button>
            </td>
          </tr>
        );
      })}
    </tbody>
  );
}

function FieldRow({ label, value }) {
  return (
    <div className="flex justify-between items-start py-2 border-b border-slate-50 last:border-0">
      <span className="text-xs font-medium text-muted-foreground w-2/5 shrink-0">{label}</span>
      <span className="text-sm text-right break-words max-w-[60%]">{value || '-'}</span>
    </div>
  );
}

function IncrDetailSheet({ sub, open, onClose }) {
  var payload = sub.raw_payload || {};
  var keys = Object.keys(payload);

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto p-0" data-testid="incr-detail-sheet">
        <div className="sticky top-0 bg-white z-10 border-b border-slate-200 px-6 py-4">
          <SheetHeader>
            <div className="flex items-start justify-between">
              <div>
                <SheetTitle className="text-xl font-bold">{sub.mapped.emp_name || 'Submission Details'}</SheetTitle>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {sub.form_name || 'Zoho Form'} &middot; {formatDate(sub.created_at)}
                </p>
              </div>
              <div className="flex gap-2">
                <Badge className={(sub.approval_status === 'accepted' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200') + ' text-xs border'} variant="outline">
                  {sub.approval_status === 'accepted' ? 'Approved' : 'Rejected'}
                </Badge>
                {sub.letter_signed && (
                  <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs border" variant="outline">
                    <FileCheck className="h-3 w-3 mr-1" /> Letter Signed
                  </Badge>
                )}
              </div>
            </div>
          </SheetHeader>
        </div>

        <div className="px-6 py-5 space-y-5">
          <div className="space-y-1">
            <div className="flex items-center gap-2 mb-2">
              <User className="h-4 w-4 text-slate-500" />
              <h4 className="text-sm font-semibold">All Submission Fields</h4>
            </div>
            <div className="bg-slate-50/50 rounded-lg px-4 py-2">
              {keys.map(function(k) {
                return <FieldRow key={k} label={k} value={payload[k]} />;
              })}
            </div>
          </div>

          <Separator />

          <div className="space-y-1">
            <h4 className="text-sm font-semibold mb-2">Approval Info</h4>
            <div className="bg-slate-50/50 rounded-lg px-4 py-2">
              <FieldRow label="Status" value={sub.approval_status === 'accepted' ? 'Approved' : 'Rejected'} />
              <FieldRow label="Approved By" value={sub.approved_by} />
              <FieldRow label="Approved At" value={formatDate(sub.approved_at)} />
              <FieldRow label="Letter Signed" value={sub.letter_signed ? 'Yes' : 'No'} />
              <FieldRow label="Submission ID" value={sub.id} />
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
